import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback } from 'react';
import styled from 'styled-components';

import ItemCardBidButton from '@/components/GridItemCard/Card/components/ItemCardBidButton/ItemCardLiveBidButton';
import NewItemCardRegisterLink from '@/components/GridItemCard/Card/components/NewItemCardRegisterLink/NewItemCardRegisterLink';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRetractBidModal } from '@/redux/modules/modal';

export type CardRegistrationProps = {
    isMobile: boolean;
    isOpenItem: boolean;
    itemId: number;
};

const CardRegistration = ({ isMobile, isOpenItem, itemId }: CardRegistrationProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const approvedWithBidLimit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));

    const isApprovedForOpenItem = isOpenItem && (approved || approvedWithBidLimit);

    const openRetractBidModalCallback = useCallback(() => {
        dispatch(openRetractBidModal(itemId));
    }, [dispatch, itemId]);

    return (
        <RegisterSection data-testid="timedPlusMyItemsCardRegistration">
            {isApprovedForOpenItem && <ItemCardBidButton itemId={itemId} />}
            {!approved && (
                <StyledRegisterButton
                    catalogId={catalogId}
                    itemId={itemId}
                    registerToBidMessageId="registration.register_for_auction_my_items"
                />
            )}
            {isApprovedForOpenItem && bidderHasBid && isMobile && (
                <StyledButton
                    aria-label="retract bid button"
                    onClick={openRetractBidModalCallback}
                    size="sm"
                >
                    {formatMessage({ id: 'retract' })}
                </StyledButton>
            )}
        </RegisterSection>
    );
};

export default CardRegistration;

const StyledRegisterButton = styled(NewItemCardRegisterLink)`
    width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 211px;
    }
`;

const RegisterSection = styled.section`
    display: flex;
    grid-area: registerForAuction;
`;

const StyledButton = styled(Button)`
    text-transform: uppercase;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding-left: 14px;
        padding-right: 14px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        padding-left: 9px;
        padding-right: 9px;
    }
`;
